import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import HistoryIcon from '@mui/icons-material/History';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import PublishOutlinedIcon from '@mui/icons-material/PublishOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { useTheme as useAppTheme } from '../../theme/ThemeProvider';
import { getPhotoUrl } from '../../utils/formatting';
import HuttonsWhite from '../../img/Huttons_white.png';
import HuttonsBlue from '../../img/Huttons_blue.png';

export type DeckDefinition = { label: string; path: string };

export const DECKS: DeckDefinition[] = [
  { label: 'Projects Pipeline', path: '/project-deck/project-pipeline' },
  { label: 'Special Promo', path: '/project-deck/special-promo' },
  { label: 'Affordable Projects', path: '/project-deck/affordable-projects' },
  { label: 'Large Floor Plate', path: '/project-deck/large-floor-plate' },
  { label: 'TOP Projects', path: '/project-deck/top-projects' },
  { label: 'GLS/Enbloc Upcoming', path: '/project-deck/gls-enbloc-upcoming' },
  { label: 'GLS/Enbloc Current', path: '/project-deck/gls-enbloc-current' },
  { label: 'Available Units', path: '/project-deck/available-units' },
];

export type DeckLoginUser = { Name?: string; Photo?: string };

export interface MasterControlsProps {
  canUndo?: boolean;
  canRedo?: boolean;
  draftSaving?: boolean;
  exporting?: boolean;
  publishing?: boolean;
  hasItems?: boolean;
  revisionsActive?: boolean;
  onUndo?: () => void;
  onRedo?: () => void;
  onRevisions?: () => void;
  onSaveDraft?: () => void;
  onExport?: () => void;
  onPublish?: () => void;
}

export function DeckNavigation({
  title = 'Projects Pipeline',
  loginUser,
}: { title?: string; loginUser?: DeckLoginUser | null }) {
  const { currentTheme, setTheme } = useAppTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isProjectsPipelineAlias = location.pathname === '/project-deck/project-pipeline'
    || location.pathname.startsWith('/project-deck/project-pipeline/');
  const activeDeck = location.pathname === '/project-deck'
    ? DECKS[0]
    : isProjectsPipelineAlias
    ? DECKS[0]
    : DECKS.find((deck, index) => index === 0
      ? location.pathname === deck.path
      : location.pathname === deck.path || location.pathname.startsWith(`${deck.path}/`));
  const activePath = activeDeck?.path ?? DECKS[0].path;

  const handleDeckChange = (event: SelectChangeEvent<string>) => navigate(event.target.value);
  const initials = (loginUser?.Name || 'U').split(/\s+/).filter(Boolean).map((part) => part[0]).slice(0, 2).join('').toUpperCase();

  return (
    <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, height: 64, bgcolor: 'var(--color-surface)', color: 'var(--color-text-strong)', display: 'flex', alignItems: 'center', gap: 2, px: { xs: 1, sm: 2, md: 4 }, zIndex: (theme) => theme.zIndex.appBar, boxShadow: '0 2px 4px rgba(var(--color-shadow-rgb), 0.3)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1.5 }, minWidth: 0 }}>
        <Box component="img" src={currentTheme === 'dark' ? HuttonsWhite : HuttonsBlue} alt="Huttons" sx={{ height: { xs: 24, sm: 28 }, width: 'auto', flexShrink: 0 }} />
        <Typography component="span" sx={{ color: 'var(--color-text-strong)', fontSize: { xs: '0.8rem', md: '0.9rem' }, whiteSpace: 'nowrap', display: { xs: 'none', sm: 'inline' } }}>
          | {title}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, sm: 1 }, ml: 'auto', flexShrink: 0 }}>
        <FormControl size="small" sx={{ minWidth: { xs: 140, sm: 220 }, '& .MuiOutlinedInput-root': { color: 'var(--color-text-strong)' }, '& .MuiSvgIcon-root': { color: 'var(--color-text-strong)' } }}>
          <Select value={activePath} onChange={handleDeckChange} aria-label="Select deck" sx={{ fontWeight: 600 }}>
            {DECKS.map((deck) => <MenuItem key={deck.path} value={deck.path}>{deck.label}</MenuItem>)}
          </Select>
        </FormControl>
        <IconButton onClick={() => setTheme(currentTheme === 'dark' ? 'light' : 'dark')} sx={{ color: 'var(--color-text-strong)' }} aria-label="Toggle theme">
          {currentTheme === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
        </IconButton>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: { xs: 0.5, sm: 1.5 } }}>
          <Avatar src={getPhotoUrl(loginUser?.Photo)} sx={{ width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 }, bgcolor: 'rgba(var(--color-text-rgb), 0.2)', color: 'var(--color-text-strong)', fontSize: '0.8rem' }}>{initials}</Avatar>
          <Typography variant="body2" fontWeight={600} sx={{ color: 'var(--color-text-strong)', display: { xs: 'none', sm: 'block' } }}>{loginUser?.Name || 'User'}</Typography>
        </Box>
      </Box>
    </Box>
  );
}

export function MasterControls({ canUndo = false, canRedo = false, draftSaving = false, exporting = false, publishing = false, hasItems = false, revisionsActive = false, onUndo, onRedo, onRevisions, onSaveDraft, onExport, onPublish }: MasterControlsProps) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0, py: 0, mb: 2, overflowX: 'auto', flexWrap: { xs: 'nowrap', md: 'wrap' }, flexShrink: 0 }}>
      <Button onClick={onUndo} disabled={!onUndo || !canUndo} startIcon={<UndoIcon />} variant="outlined" size="small" sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}>Undo</Button>
      <Button onClick={onRedo} disabled={!onRedo || !canRedo} startIcon={<RedoIcon />} variant="outlined" size="small" sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}>Redo</Button>
      <Button onClick={onRevisions} disabled={!onRevisions} variant={revisionsActive ? 'contained' : 'outlined'} size="small" startIcon={<HistoryIcon />} sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}>Revisions</Button>
      <Button onClick={onSaveDraft} disabled={!onSaveDraft || !hasItems || draftSaving} startIcon={draftSaving ? <CircularProgress size={16} /> : <SaveOutlinedIcon />} variant="outlined" size="small" sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}>Save current draft</Button>
      <Button onClick={onPublish} disabled={!onPublish || !hasItems || publishing || exporting} startIcon={publishing ? <CircularProgress size={16} /> : <PublishOutlinedIcon />} variant="outlined" size="small" sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}>Publish</Button>
      <Button onClick={onExport} disabled={!onExport || !hasItems || exporting || publishing} startIcon={<PictureAsPdfIcon />} variant="outlined" size="small" sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}>Generate PDF</Button>
    </Box>
  );
}

export function DeckComingSoon({ deck }: { deck: DeckDefinition }) {
  return <Box sx={{ minHeight: '100vh', bgcolor: 'var(--color-bg)', color: 'var(--color-text-strong)' }}><DeckNavigation title={deck.label} /><Box sx={{ pt: '64px' }}><Box sx={{ px: 3, pt: 5, textAlign: 'center' }}><Typography variant="h4" fontWeight={700}>{deck.label}</Typography><Typography sx={{ mt: 2 }} color="text.secondary">This deck is coming soon.</Typography></Box></Box></Box>;
}

export function getDeckByPath(pathname: string): DeckDefinition {
  if (pathname === '/project-deck/project-pipeline' || pathname.startsWith('/project-deck/project-pipeline/')) {
    return DECKS[0];
  }
  return DECKS.find((deck, index) => index === 0
    ? pathname === deck.path
    : pathname === deck.path || pathname.startsWith(`${deck.path}/`)) || DECKS[0];
}

export default DeckNavigation;
